import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPaperPlane, FaUserCircle, FaPaperclip, FaDownload } from 'react-icons/fa';
import { MdGroup, MdPerson } from 'react-icons/md';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { formatDate } from '../../utils/helpers';

/**
 * Message Center component for mentors to communicate with students, parents and other staff
 */
const MessageCenter = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [recipients, setRecipients] = useState([]);
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [composeData, setComposeData] = useState({
    subject: '',
    message: '',
    recipients: []
  });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Fetch all conversations
   */
  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mentors/messages/conversations');
      setConversations(response.data.data);
      
      // Select the first conversation if available
      if (response.data.data.length > 0 && !selectedConversation) {
        setSelectedConversation(response.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      showToast('Failed to load conversations', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch messages for a specific conversation
   */
  const fetchMessages = async (conversationId) => {
    try {
      setLoading(true);
      const response = await api.get(`/mentors/messages/conversations/${conversationId}`);
      setMessages(response.data.data.messages);
      
      // Mark conversation as read
      await api.put(`/mentors/messages/conversations/${conversationId}/read`);
      
      // Update conversations list to reflect read status
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      );
    } catch (error) {
      console.error('Error fetching messages:', error);
      showToast('Failed to load messages', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch potential recipients for new messages
   */
  const fetchRecipients = async () => {
    try {
      const response = await api.get('/mentors/messages/recipients');
      setRecipients(response.data.data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      showToast('Failed to load recipients', 'error');
    }
  };

  /**
   * Handle selecting a conversation
   */
  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  /**
   * Send a new message in the current conversation
   */
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() && attachments.length === 0) return;
    
    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      
      // Append attachments if any
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      await api.post(`/mentors/messages/conversations/${selectedConversation._id}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Clear form and refresh messages
      setNewMessage('');
      setAttachments([]);
      fetchMessages(selectedConversation._id);
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Failed to send message', 'error');
    }
  };

  /**
   * Handle file selection for attachments
   */
  const handleFileSelect = (e) => {
    setAttachments(Array.from(e.target.files));
  };

  /**
   * Handle changes in the compose message form
   */
  const handleComposeChange = (e) => {
    const { name, value } = e.target;
    setComposeData({
      ...composeData,
      [name]: value
    });
  };

  /**
   * Handle recipient selection in compose form
   */
  const handleRecipientChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
    setComposeData({
      ...composeData,
      recipients: selectedOptions
    });
  };

  /**
   * Open the compose message modal
   */
  const handleComposeClick = () => {
    fetchRecipients();
    setComposeData({
      subject: '',
      message: '',
      recipients: []
    });
    setIsComposeModalOpen(true);
  };

  /**
   * Start a new conversation
   */
  const handleStartConversation = async (e) => {
    e.preventDefault();
    
    if (!composeData.subject.trim() || !composeData.message.trim() || composeData.recipients.length === 0) {
      showToast('Please fill in all fields', 'error');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('subject', composeData.subject);
      formData.append('message', composeData.message);
      
      // For single recipient
      const recipientId = composeData.recipients[0];
      
      const response = await api.post(`/mentors/messages/parents/${recipientId}`, {
        subject: composeData.subject,
        message: composeData.message
      });
      
      // Close modal and refresh conversations
      setIsComposeModalOpen(false);
      fetchConversations();
      
      // Select the new conversation
      setSelectedConversation(response.data.data);
      
      showToast('Message sent successfully', 'success');
    } catch (error) {
      console.error('Error starting conversation:', error);
      showToast('Failed to send message', 'error');
    }
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /**
   * Handle filter selection change
   */
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  /**
   * Handle attachment download
   */
  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/mentors/messages/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showToast('Failed to download attachment', 'error');
    }
  };

  /**
   * Format date for display
   */
  const formatDateTime = (dateString) => {
    return formatDate(dateString);
  };

  /**
   * Get the recipient name from conversation
   */
  const getRecipientName = (conversation) => {
    if (!conversation || !conversation.recipient) return 'Unknown';
    
    const recipient = conversation.recipient;
    return `${recipient.firstName} ${recipient.lastName}`;
  };

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden h-[calc(100vh-180px)] flex flex-col">
      <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800">Message Center</h2>
        <button 
          onClick={handleComposeClick}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
        >
          Compose Message
        </button>
      </div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Conversations list */}
        <div className="w-1/3 border-r overflow-y-auto">
          <div className="p-3 border-b">
            <div className="flex items-center bg-gray-100 rounded-md p-2">
              <FaSearch className="text-gray-500 mr-2" />
              <input 
                type="text" 
                placeholder="Search conversations..." 
                className="bg-transparent border-none focus:outline-none w-full"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          
          <div className="flex justify-end p-2">
            <select 
              className="form-select text-sm"
              value={filter}
              onChange={handleFilterChange}
            >
              <option value="all">All</option>
              <option value="unread">Unread</option>
              <option value="parents">Parents</option>
              <option value="recent">Recent</option>
            </select>
          </div>
          
          {loading && !conversations.length ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div>
              {conversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No conversations found</p>
                  <button 
                    onClick={handleComposeClick}
                    className="mt-2 text-blue-600 hover:underline"
                  >
                    Start a new conversation
                  </button>
                </div>
              ) : (
                <ul>
                  {conversations.map(conversation => (
                    <li 
                      key={conversation._id}
                      className={`border-b cursor-pointer p-3 hover:bg-gray-50 ${
                        selectedConversation?._id === conversation._id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      } ${conversation.unreadCount > 0 ? 'font-semibold' : ''}`}
                      onClick={() => selectConversation(conversation)}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <div className="font-medium text-gray-900 truncate max-w-[70%]">
                          {getRecipientName(conversation)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDateTime(conversation.lastMessageDate)}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 truncate">{conversation.subject}</div>
                      <div className="text-xs text-gray-500 truncate mt-1">{conversation.lastMessage}</div>
                      {conversation.unreadCount > 0 && (
                        <div className="mt-1">
                          <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                            {conversation.unreadCount} new
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        
        {/* Message display */}
        <div className="w-2/3 flex flex-col h-full">
          {!selectedConversation ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <p>Select a conversation to view messages</p>
                <p className="mt-2">or</p>
                <button 
                  onClick={handleComposeClick}
                  className="mt-2 text-blue-600 hover:underline"
                >
                  Start a new conversation
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 border-b">
                <h3 className="font-medium">{getRecipientName(selectedConversation)}</h3>
                <p className="text-sm text-gray-600">{selectedConversation.subject}</p>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    {messages.map((message) => (
                      <div 
                        key={message._id} 
                        className={`flex ${message.sender._id === user?._id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-[80%] rounded-lg p-3 ${
                            message.sender._id === user?._id 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <div className="flex items-center mb-1">
                            {message.sender._id !== user?._id && (
                              <>
                                {message.sender.profileImage ? (
                                  <img 
                                    src={message.sender.profileImage} 
                                    alt={`${message.sender.firstName} ${message.sender.lastName}`}
                                    className="w-6 h-6 rounded-full mr-2"
                                  />
                                ) : (
                                  <FaUserCircle className="w-6 h-6 mr-2 text-gray-500" />
                                )}
                                <span className="text-xs font-medium mr-2">
                                  {message.sender.firstName} {message.sender.lastName}
                                </span>
                              </>
                            )}
                            <span className={`text-xs ${message.sender._id === user?._id ? 'text-blue-100' : 'text-gray-500'}`}>
                              {formatDateTime(message.createdAt)}
                            </span>
                          </div>
                          
                          <div>{message.content}</div>
                          
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {message.attachments.map((attachment, index) => (
                                <div 
                                  key={index} 
                                  className={`flex items-center text-xs p-1 rounded ${
                                    message.sender._id === user?._id ? 'bg-blue-400' : 'bg-gray-200'
                                  }`}
                                >
                                  <FaPaperclip className="mr-1" />
                                  <span className="truncate flex-1">{attachment.originalname}</span>
                                  <button
                                    onClick={() => handleDownloadAttachment(attachment._id, attachment.originalname)}
                                    className={`ml-2 p-1 rounded ${
                                      message.sender._id === user?._id ? 'hover:bg-blue-600' : 'hover:bg-gray-300'
                                    }`}
                                  >
                                    <FaDownload />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              <form onSubmit={handleSendMessage} className="p-3 border-t">
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-500 hover:text-blue-500"
                  >
                    <FaPaperclip />
                  </button>
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    className="hidden"
                  />
                  <input
                    type="text"
                    placeholder="Type a message..."
                    className="flex-1 border rounded-md p-2 mx-2"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600"
                  >
                    <FaPaperPlane />
                  </button>
                </div>
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="bg-gray-100 text-xs rounded p-1 flex items-center">
                        <span className="truncate max-w-[150px]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}
                          className="ml-1 text-red-500 hover:text-red-700"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
      
      {/* Compose Modal */}
      <Modal
        isOpen={isComposeModalOpen}
        onClose={() => setIsComposeModalOpen(false)}
        title="New Message"
      >
        <form onSubmit={handleStartConversation} className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Recipients</label>
            <select
              name="recipients"
              multiple
              className="form-select w-full"
              onChange={handleRecipientChange}
              value={composeData.recipients}
            >
              {recipients.map(recipient => (
                <option key={recipient._id} value={recipient._id}>
                  {recipient.firstName} {recipient.lastName} ({recipient.role})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple recipients</p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              className="form-input w-full"
              value={composeData.subject}
              onChange={handleComposeChange}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              name="message"
              rows="5"
              className="form-textarea w-full"
              value={composeData.message}
              onChange={handleComposeChange}
            ></textarea>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsComposeModalOpen(false)}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded mr-2 hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Send Message
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MessageCenter; 